import { cuerpoDeCarga, faltanDeCarga, type FormularioCarga } from './carga';
/**
 * Armado de las peticiones a la API desde el estado del formulario.
 *
 * Logica pura y probada, separada de la vista por un motivo concreto: es donde
 * se decide QUE se manda, y dos de esas decisiones tienen consecuencias en el
 * dominio.
 *
 *   1. `pol_pais` se omite cuando el agente no lo eligio. No se rellena con un
 *      valor por defecto ni se deduce del ultimo puerto usado. Mandar un pais
 *      inventado convierte el 409 de "Manzanillo" en una busqueda que responde
 *      200 y manda la carga al continente equivocado, en silencio.
 *      [Invariante 6][H51]
 *
 *   2. Las validaciones de aqui son un atajo, no la autoridad. La API vuelve a
 *      validar todo y sus mensajes son los que mandan; esto solo evita el viaje
 *      cuando el formulario esta a medias. Por eso los nombres de los campos
 *      que faltan son los NOMBRES DE LA API (`fecha_embarque`, no `fecha`): la
 *      pantalla marca el mismo campo venga el error de donde venga.
 *
 * ---------------------------------------------------------------------------
 * `usuario_id` YA NO SE ARMA AQUI
 *
 * Hasta la migracion 0020 las tres peticiones de este archivo llevaban dentro
 * el id de quien las hacia, y este modulo lo validaba como un campo mas del
 * formulario. Ahora quien llama sale de la cookie de sesion, en el servidor
 * [services/api/src/servidor.ts]: no se manda, y si se mandara la API lo
 * ignoraria. Un campo que el navegador ya no puede elegir no tiene nada que
 * hacer en el modulo que decide QUE se manda.
 */

/** Espejo de MAX_UNIDADES en services/api/src/cotizacion.ts. Duplicado a
 *  proposito y marcado como tal: el navegador no puede importar del servicio.
 *  Si divergen, manda la API --devolvera 400 y la pantalla lo pintara--. */
export const MAX_UNIDADES = 999;

const ISO_FECHA = /^\d{4}-\d{2}-\d{2}$/;
const PAIS = /^[A-Za-z]{2}$/;

export type Armado<T> =
  | { readonly ok: true; readonly cuerpo: T }
  | { readonly ok: false; readonly faltan: readonly string[] };

export interface FormularioBusqueda {
  readonly pol: string;
  readonly polPais: string | null;
  readonly pod: string;
  readonly podPais: string | null;
  readonly modo: string;
  /** Solo maritimo. En aereo no hay equipo que ofrecer. */
  readonly equipo: string;
  /**
   * Solo aereo, y obligatorios ahi.
   *
   * Los dos, siempre: el peso que se cobra es el mayor entre el de la bascula y
   * el que sale del volumen, y con uno solo no se sabe cual es. Un volumen
   * ausente tomado como cero haria ganar siempre al real y facturaria de menos
   * [H80].
   */
  readonly pesoKg: string;
  readonly volumenM3: string;
  /** Solo aereo y OPCIONAL. No entra en ningun precio: sirve para contrastar
   *  los limites por pieza de cada tarifa [H85]. */
  readonly bultos: string;
  readonly fechaEmbarque: string;
  /**
   * Que es lo que viaja. De aqui salen las MEDIDAS de un bulto cuando la carga
   * es sobredimensionada, que es la otra mitad de la comprobacion de [H85].
   *
   * NO filtra tarifas y no se valida aqui: buscar con una declaracion a medias
   * es legitimo --lo que no se puede es cotizar o mandar un RFQ asi--. Lo unico
   * que se manda son las medidas, y solo cuando la naturaleza las pide.
   */
  readonly carga: FormularioCarga;
}

function limpio(v: string | null | undefined): string | undefined {
  if (v === null || v === undefined) return undefined;
  const t = v.trim();
  return t === '' ? undefined : t;
}

/** Un decimal positivo con hasta tres cifras. Mismo patron que el de la API,
 *  que es la que manda: esto solo ahorra el viaje. */
const POSITIVO = /^\d{1,12}(\.\d{1,3})?$/;
const ENTERO = /^\d{1,6}$/;

/**
 * Que campos exige cada modo.
 *
 * El modo decide QUE MAS hace falta, y por eso se resuelve antes que nada:
 * pedir "equipo" en una busqueda aerea manda al agente a buscar un campo que no
 * existe en su formulario, y pedir el peso en una FCL, lo mismo al reves.
 */
export function esBusquedaAerea(modo: string): boolean {
  return limpio(modo) === 'AEREO';
}

export function armarBusqueda(f: FormularioBusqueda): Armado<Record<string, unknown>> {
  const pol = limpio(f.pol);
  const pod = limpio(f.pod);
  const modo = limpio(f.modo);
  const equipo = limpio(f.equipo);
  const fecha = limpio(f.fechaEmbarque);
  const aereo = esBusquedaAerea(f.modo);

  const faltan: string[] = [];
  if (pol === undefined) faltan.push('pol');
  if (pod === undefined) faltan.push('pod');
  if (modo === undefined) faltan.push('modo');
  if (!aereo && equipo === undefined) faltan.push('equipo');

  // Las cifras de la carga aerea. Una mal escrita cuenta como ausente, igual
  // que la fecha: para el agente las dos se arreglan en el mismo sitio.
  const pesoKg = limpio(f.pesoKg);
  const volumenM3 = limpio(f.volumenM3);
  const bultos = limpio(f.bultos);
  if (aereo) {
    if (pesoKg === undefined || !POSITIVO.test(pesoKg) || Number(pesoKg) <= 0) {
      faltan.push('peso_kg');
    }
    if (volumenM3 === undefined || !POSITIVO.test(volumenM3)) faltan.push('volumen_m3');
    // Opcional, pero si se escribe tiene que ser una cuenta de piezas.
    if (bultos !== undefined && (!ENTERO.test(bultos) || Number(bultos) < 1)) {
      faltan.push('bultos');
    }
  }
  // Una fecha mal escrita cuenta como ausente y no como "invalida" aparte: para
  // el agente las dos se arreglan en el mismo sitio, y la fecha decide que
  // tarifas estan vigentes, asi que no se supone "hoy" en ningun caso.
  if (fecha === undefined || !ISO_FECHA.test(fecha)) faltan.push('fecha_embarque');

  const polPais = limpio(f.polPais);
  const podPais = limpio(f.podPais);
  if (polPais !== undefined && !PAIS.test(polPais)) faltan.push('pol_pais');
  if (podPais !== undefined && !PAIS.test(podPais)) faltan.push('pod_pais');

  if (faltan.length > 0) return { ok: false, faltan };

  // Las medidas de un bulto, cuando la naturaleza declarada las trae. Se mandan
  // SOLO en aereo: en maritimo la API no comprueba limites por pieza y serian
  // tres campos que viajan para nada.
  //
  // Salen de `cuerpoDeCarga` y no de los campos del formulario a pelo, que es
  // lo que garantiza que una medida de una naturaleza anterior no se cuele
  // [carga.ts]: cambiar a PELIGROSA deja de mandarlas en el mismo momento.
  const medidas = aereo ? cuerpoDeCarga(f.carga) : {};
  const dimension = (campo: string): Record<string, unknown> =>
    medidas[campo] === undefined ? {} : { [campo]: medidas[campo] };

  return {
    ok: true,
    cuerpo: {
      pol,
      pod,
      modo,
      // En aereo no viaja: no hay equipo que ofrecer, y mandar una cadena vacia
      // haria que la API contestara que falta un campo obligatorio.
      ...(aereo ? {} : { equipo }),
      ...(aereo
        ? {
            peso_kg: pesoKg,
            volumen_m3: volumenM3,
            ...(bultos === undefined ? {} : { bultos }),
            ...dimension('dim_largo_cm'),
            ...dimension('dim_ancho_cm'),
            ...dimension('dim_alto_cm'),
          }
        : {}),
      fecha_embarque: fecha,
      // Solo cuando hay pais elegido. Un `pol_pais: null` explicito seria un
      // campo mas que la API tiene que ignorar; la ausencia ya significa
      // "no elegido" y es lo que dispara la desambiguacion.
      ...(polPais === undefined ? {} : { pol_pais: polPais.toUpperCase() }),
      ...(podPais === undefined ? {} : { pod_pais: podPais.toUpperCase() }),
    },
  };
}

/** El peso, el volumen y los bultos con los que se BUSCO. Ver `armarCotizacion`. */
export interface CargaAereaCotizacion {
  readonly peso_kg: string;
  readonly volumen_m3: string;
  readonly bultos: string | null;
}

export interface FormularioCotizacion {
  readonly rateRecordId: string | null;
  /** Nulo en aereo: no hay equipo que cotizar, y mandarlo hace que la API
   *  conteste 400 --una tarifa aerea no lleva caja--. */
  readonly equipo: string | null;
  /**
   * La carga con la que se busco, en aereo. Nula en maritimo.
   *
   * Los MISMOS numeros de la busqueda y no los de un campo que se pueda haber
   * tocado despues: el escalon y el precio por kilo que el agente vio salieron
   * de estas dos cifras, y cotizar con otras daria un documento que no se
   * parece a la pantalla de la que salio [H8][H80].
   */
  readonly aerea: CargaAereaCotizacion | null;
  readonly unidades: number | null;
  readonly clienteId: string | null;
  readonly idioma: string;
  /**
   * Que es lo que viaja, ya armado por `cuerpoDeCarga` [carga.ts].
   *
   * Llega hecho y no como formulario: la validacion de la carga es de la carga
   * --que campo hace falta con que naturaleza-- y meterla aqui la duplicaria en
   * un archivo que no la conoce. Aqui solo se pega al cuerpo.
   */
  readonly carga?: Record<string, unknown>;
}

export function armarCotizacion(
  f: FormularioCotizacion,
): Armado<Record<string, unknown>> {
  const faltan: string[] = [];

  const rateRecordId = limpio(f.rateRecordId);
  const equipo = limpio(f.equipo);
  const clienteId = limpio(f.clienteId);
  const aerea = f.aerea;

  if (rateRecordId === undefined) faltan.push('rate_record_id');
  // El equipo solo cuando NO es aerea. Las dos ramas son excluyentes y la API
  // rechaza la mezcla: una tarifa aerea con equipo, o una maritima con peso,
  // significan que quien llama cree estar cotizando otra cosa.
  if (aerea === null && equipo === undefined) faltan.push('equipo');
  if (
    aerea !== null &&
    (limpio(aerea.peso_kg) === undefined || limpio(aerea.volumen_m3) === undefined)
  ) {
    faltan.push('peso_kg');
  }
  if (clienteId === undefined) faltan.push('cliente_id');

  // Entero: en FCL se embarcan contenedores completos. Un 2.5 no es medio
  // contenedor mas caro, es un formulario mal llenado. En aereo el
  // multiplicador son los kilos y esto vale uno, que es lo que manda la API.
  const u = aerea === null ? f.unidades : 1;
  if (u === null || !Number.isInteger(u) || u < 1 || u > MAX_UNIDADES) {
    faltan.push('unidades');
  }

  if (faltan.length > 0) return { ok: false, faltan };

  return {
    ok: true,
    cuerpo: {
      rate_record_id: rateRecordId,
      ...(aerea === null
        ? { equipo }
        : {
            peso_kg: aerea.peso_kg,
            volumen_m3: aerea.volumen_m3,
            // Entero para la API: es una cuenta de piezas. Ausente cuando no se
            // declaro, nunca cero.
            ...(aerea.bultos === null ? {} : { bultos: Number(aerea.bultos) }),
          }),
      unidades: u,
      cliente_id: clienteId,
      ...(f.carga ?? {}),
      // [CLAUDE.md, Idioma] Congela el idioma en que sale el documento. Es el
      // idioma que el agente tiene puesto AL COTIZAR, no una preferencia que
      // se relea despues.
      idioma: f.idioma,
    },
  };
}

/**
 * La solicitud de cotizacion a proveedores.
 *
 * Dos decisiones con consecuencia, las mismas que en `armarBusqueda`:
 *
 *   1. El pais viaja cuando se eligio y no se rellena nunca. Un RFQ a
 *      "Manzanillo" sin pais es un correo que puede acabar pidiendo precio para
 *      el oceano equivocado, y eso se descubre cuando contesta el proveedor.
 *      [Invariante 6][H51]
 *
 *   2. Los decimales viajan COMO CADENA, tal como los escribio el agente. El
 *      peso decide el break y el recargo por sobrepeso [H25]; convertirlo a
 *      `number` para "validarlo" lo metaria en el punto flotante de JavaScript
 *      justo en el borde donde CLAUDE.md dice que no entra. Se valida con una
 *      expresion regular y se manda el texto.
 */
export interface FormularioRfq {
  readonly pol: string;
  readonly polPais: string | null;
  readonly pod: string;
  readonly podPais: string | null;
  readonly modo: string;
  readonly equipo: string;
  readonly unidades: number | null;
  readonly mercancia: string;
  readonly requisitos: string;
  readonly pesoKg: string;
  readonly volumenM3: string;
  readonly temperaturaC: string;
  readonly fechaObjetivo: string;
  readonly nota: string;
  readonly remitente: string;
  /** Los proveedores marcados. Sin ninguno no hay peticion que mandar. */
  readonly proveedores: readonly string[];
  /**
   * Que es lo que viaja. Se precarga de lo declarado en el buscador y se puede
   * afinar aqui: un RFQ se manda a veces por una carga distinta de la que se
   * buscaba.
   *
   * Su validacion vive en `carga.ts`, que es donde esta el criterio de que
   * campo hace falta con que naturaleza.
   */
  readonly carga: FormularioCarga;
}

/** Un decimal con hasta tres cifras y signo opcional. La API aplica el mismo
 *  patron; esto solo ahorra el viaje. */
const DECIMAL = /^-?\d{1,12}(\.\d{1,3})?$/;

export function armarSolicitudRfq(f: FormularioRfq): Armado<Record<string, unknown>> {
  const pol = limpio(f.pol);
  const pod = limpio(f.pod);
  const modo = limpio(f.modo);

  const faltan: string[] = [];
  if (pol === undefined) faltan.push('pol');
  if (pod === undefined) faltan.push('pod');
  if (modo === undefined) faltan.push('modo');
  if (f.proveedores.length === 0) faltan.push('destinatarios');

  const fecha = limpio(f.fechaObjetivo);
  if (fecha !== undefined && !ISO_FECHA.test(fecha)) faltan.push('fecha_objetivo');

  const decimales: [string, string | undefined][] = [
    ['peso_kg', limpio(f.pesoKg)],
    ['volumen_m3', limpio(f.volumenM3)],
    ['temperatura_c', limpio(f.temperaturaC)],
  ];
  for (const [campo, valor] of decimales) {
    if (valor !== undefined && !DECIMAL.test(valor)) faltan.push(campo);
  }

  const u = f.unidades;
  if (u !== null && (!Number.isInteger(u) || u < 1 || u > MAX_UNIDADES)) {
    faltan.push('unidades');
  }

  const polPais = limpio(f.polPais);
  const podPais = limpio(f.podPais);
  if (polPais !== undefined && !PAIS.test(polPais)) faltan.push('pol_pais');
  if (podPais !== undefined && !PAIS.test(podPais)) faltan.push('pod_pais');

  // Lo que exija la naturaleza declarada, con los mismos nombres de campo que
  // el resto: quien pinta marca el borde igual. La busqueda NO lo comprueba: la
  // carga no filtra tarifas, asi que buscar con una declaracion a medias es
  // legitimo --lo que no se puede es mandar el RFQ asi--.
  faltan.push(...faltanDeCarga(f.carga));

  if (faltan.length > 0) return { ok: false, faltan };

  const opcional = (campo: string, valor: string | undefined): Record<string, string> =>
    valor === undefined ? {} : { [campo]: valor };

  return {
    ok: true,
    cuerpo: {
      modo,
      pol,
      pod,
      ...opcional('pol_pais', polPais?.toUpperCase()),
      ...opcional('pod_pais', podPais?.toUpperCase()),
      ...opcional('equipo', limpio(f.equipo)),
      ...(u === null ? {} : { unidades: u }),
      ...opcional('mercancia', limpio(f.mercancia)),
      ...opcional('requisitos', limpio(f.requisitos)),
      ...opcional('peso_kg', limpio(f.pesoKg)),
      ...opcional('volumen_m3', limpio(f.volumenM3)),
      ...opcional('temperatura_c', limpio(f.temperaturaC)),
      ...opcional('fecha_objetivo', fecha),
      ...opcional('nota', limpio(f.nota)),
      // Solo lo que corresponde a la naturaleza elegida. Un dato de la anterior
      // acabaria en el correo al proveedor, y el proveedor cotizaria un reefer
      // que nadie pidio [carga.ts].
      ...cuerpoDeCarga(f.carga),
      remitente: f.remitente,
      // Solo el proveedor: a que persona se le escribe lo decide el directorio,
      // que es donde vive el contacto marcado para RFQ. La pantalla no elige
      // destinatario, elige proveedor.
      destinatarios: f.proveedores.map((id) => ({ proveedor_id: id })),
    },
  };
}

/**
 * La query string del autocompletado.
 *
 * Devuelve `null` por debajo de dos caracteres: un prefijo de una letra pide
 * medio catalogo para nada. La API aplica el mismo minimo --esto solo ahorra
 * el viaje--.
 */
export function armarSugerencias(q: string, modo: string): string | null {
  const termino = q.trim();
  if (termino.length < 2 || limpio(modo) === undefined) return null;
  return `q=${encodeURIComponent(termino)}&modo=${encodeURIComponent(modo.trim())}`;
}
